<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import Tabs from "~/components/ui/Tabs.vue";
import { useBetbook } from "~/store/betbook";
import { ISport } from "~/types/sports/sport";
import EventFiltersModal from "~/components/layout/Sport/SportsBook/EventFilters/Modal/index.vue";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import t from "~/utils/translate";
import LiveIndicatorDotDisabled from "~/components/layout/Sport/LiveIndicatorDotDisabled.vue";
import LiveIndicatorDot from "~/components/layout/Sport/LiveIndicatorDot.vue";
import KenticoClientContent from "~/components/global/kentico/components/KenticoClientContent.vue";
import { useSettings } from "~/store/settings";
import api from "~/utils/auth/api";
import { IEvent } from "~/types/sports/event";
import { IMarket } from "~/types/sports/market";
import { IOutcome } from "~/types/sports/outcome";
import { IPrice } from "~/types/sports/price";
import { IScore } from "~/types/sports/score";
import { IRegion } from "~/types/sports/region";
import BetbookSignalRCallback from "~/composables/SignalR/callbacks/BetbookSignalRCallback";
import { signalREndpoints, useSignalR } from "~/composables/SignalR/useSignalR";
import SimpleViewAccordion from "./SportsBook/EventSimpleView/SimpleViewAccordion.vue";
import EventSimpleView from "./SportsBook/EventSimpleView/index.vue";
import EventLoadingSimple from "./SportsBook/EventLoadingSimple.vue";
import { mdiChevronDown } from "@mdi/js";
import Button from "~/components/ui/Button.vue";

interface Props {
	sports: { sports: ISport[] } | undefined;
	isSportsLoading: boolean;
	isEsports: boolean;
	liveOnly?: boolean;
}

const store = useBetbook();
const props = defineProps<Props>();
const router = useRouter();
const route = useRoute();
const settings = useSettings();
const betbookSignalR = useSignalR("betBook", BetbookSignalRCallback);
const ledger = useLedger();
const eventIds = ref<{ [id: number]: number[] }>({});

const sports = computed(() => {
	return props.sports?.sports.filter((x) => x.sportType === "Sport");
});

const sportId = computed(() => {
	if (route.params.sportId) return route.params.sportId;
	else if (props.liveOnly && !route.params.sportId) return props.sports?.sports.find((x) => x.liveInPlayCount > 0)?.sportId;
	else return sports.value?.[0].sportId;
});

const sport = computed(() => {
	if (sports && sports.value?.[0]) {
		const val = sportId ? sports.value?.find((x) => x.sportId === sportId.value) : sports.value?.[0];

		if (val?.filterMarkets !== store.filters.markets && val) {
			store.filters.markets = val?.filterMarkets || [];
			store.calculateDisplayMarkets(val?.filterMarkets || []);
		} else {
			store.filters.markets = sports.value?.[0].filterMarkets || [];
			store.calculateDisplayMarkets(sports.value?.[0].filterMarkets || []);
		}
		return val;
	}
});

const handleFiltersClose = (hasChanges: boolean) => {
	store.filterModal = false;
};

const handleRetry = () => {
	window.location.reload();
};

interface IBetbookCall {
	isFinalPage: boolean;
	isFinalPageFiltered: boolean;
	events: IEvent[];
	markets: IMarket[];
	outcomes: IOutcome[];
	outrightRegions: {
		name: string;
		regionId: string;
		sportId: string;
		sportEvents: IEvent[];
	}[];
	prices: IPrice[];
	scores: IScore[];
	regions: IRegion[];
}

onMounted(async () => {
	eventIds.value = {};
	await Promise.all([
		(store.events = new Map()),
		(store.markets = new Map()),
		(store.outcomes = new Map()),
		(store.prices = new Map()),
		(store.scores = new Map()),
	]);
});

const reactiveFilters = computed(() => store.filters);

const handleSignalRunsubscribe = () => {
	const ledgerData = ledger.get("events", "betBook");
	if (ledgerData) {
		betbookSignalR.unSubscribe(signalREndpoints.unsubscriptionBetbookEndpoint, {
			eventIds: ledgerData.events,
			marketTypes: ledgerData.markets,
			countryCode: settings.getCountryConfig?.countryIsoTwo,
		});
	}
};

onBeforeUnmount(() => {
	handleSignalRunsubscribe();
});

const fixtureTypeMap: { [x: number]: string } = {
	0: "LiveInPlay",
	1: "Highlights",
};

const betBookCall = (fixtureType: number) => {
	return api<IBetbookCall>({
		url: `/BetBook/${fixtureTypeMap[fixtureType]}?countryCode=${settings.getCountryConfig?.countryIsoTwo}&sportId=${
			sport.value?.sportId
		}&Skip=0&Take=10&cultureCode=${settings.locale}&isEsport=${props.isEsports}&boostedOnly=false&marketTypes=${encodeURIComponent(
			reactiveFilters.value.markets[0].name
		)}`,
		type: "sports",
		apiVersion: "v1",
	}).then(async (data) => {
		try {
			eventIds.value = {
				...eventIds.value,
				[fixtureType]: data.events.map((e) => e.eventId).slice(0, 10),
			};

			await Promise.all([
				store.setEvents(data.events, true),
				store.setMarkets(data.markets, true),
				store.setOutcomes(data.outcomes, true),
				store.setPrices(data.prices, true),
				store.setScores(data.scores, true),
				store.setRegions(data.regions, true),
			]);
			const active = [...eventIds.value[0], ...eventIds.value[1]];
			betbookSignalR.subscribe(
				signalREndpoints.subscriptionBetbookEndpoint,
				active,
				{
					eventIds: active,
					marketTypes: [reactiveFilters.value.markets[0].name],
					countryCode: settings.getCountryConfig?.countryIsoTwo,
				},
				[reactiveFilters.value.markets[0].name]
			);
		} catch (e) {}
		return data;
	});
};

const { isLoading: isLoadingHilights, error: highlightsError } = useQuery({
	queryKey: ["simple-view-highlights", sport, reactiveFilters],
	queryFn: () => betBookCall(1),
	cacheTime: 0,
	enabled: computed(() => !!(reactiveFilters.value && reactiveFilters.value.markets.length)),
});

const { isLoading: isLoadingLive, error: liveError } = useQuery({
	queryKey: ["simple-view-live", sport, reactiveFilters],
	queryFn: () => betBookCall(0),
	cacheTime: 0,
	refetchOnWindowFocus: false,
	refetchOnReconnect: false,
	enabled: computed(() => !!(reactiveFilters.value && reactiveFilters.value.markets.length)),
});

useQuery({
	queryKey: ["sport-regions", sportId],
	queryFn: () =>
		api<{ regions: IRegion[] }>({
			url: `/Feeds/RegionsAndLeagues/${sportId.value}?countryCode={brand}`,
			apiVersion: "v1",
			type: "sports",
		}).then((res) => {
			store.setRegions(res.regions);
			return res;
		}),
	enabled: computed(() => !!sportId.value),
});
</script>

<template>
	<div id="sports-container" class="flex flex-col gap-2">
		<NuxtErrorBoundary>
			<template #error="{ error }">
				<div class="flex flex-col items-center justify-center w-full gap-2 my-6 text-center">
					<strong>Unexpected Error</strong>
					<p class="text-xs">
						{{ error }}
					</p>
					<UiButton label="Try Again" @click="handleRetry()" class="w-48" />
				</div>
			</template>
			<EventFiltersModal
				v-if="store.filterModal"
				:sport="sport"
				@close="handleFiltersClose"
				@go-to-highlights="
					(query) => {
						router
							.push({
								path: sportUrlBuilder({ sport: sport?.sportId, fixtureType: 'highlights' }),
								query,
							})
							.then(() => (store.applyingFilters = false));
					}
				"
			/>
			<Tabs
				id="sports-tabs"
				class="text-sm"
				tab-class="justify-center flex-1"
				:active="''"
				@on-update="
					(selected) => {
						store.applyingFilters = true;
						store.resetPagination();
						router.push(sportUrlBuilder({ sport: sport?.sportId, fixtureType: selected })).then(() => (store.applyingFilters = false));
					}
				"
				:tabs="[
					{
						name: t('live'),
						value: 'live',
						icon: sport?.hasLiveInPlayEvents ? LiveIndicatorDot : LiveIndicatorDotDisabled,
						disabled: !sport?.hasLiveInPlayEvents,
					},
					{ name: t('highlights'), value: 'highlights', disabled: !sport?.hasHighlights },
					{ name: t('upcoming'), value: 'upcoming', disabled: !sport?.hasUpcomingEvents },
					{ name: t('outrights'), value: 'outrights', disabled: !sport?.hasOutrights },
				]"
			/>
			<SimpleViewAccordion :title="t('highlights')">
				<EventLoadingSimple
					v-if="isLoadingHilights"
					v-for="loader in Array.from({ length: 10 }).keys()"
					:key="loader"
					:class="['border-t first:border-none border-light-500 dark:border-dark-600']"
				/>
				<EventSimpleView
					:class="['border-t first:border-none border-light-500 dark:border-dark-600']"
					v-else-if="!highlightsError && eventIds[1] && eventIds[1].length"
					v-for="event in eventIds[1]"
					:key="`simple-combined-event-${event}`"
					:eventId="event"
				/>
				<div class="flex items-center justify-center p-2 bg-light-50 dark:bg-dark-800" v-else>{{ t("no-results-found") }}</div>
				<div
					class="flex items-center justify-center w-full p-1 border-t rounded-b bg-light-50 dark:bg-dark-800 border-t-light-300 dark:border-t-dark-700"
				>
					<Button
						@click="router.push(sportUrlBuilder({ sport: sport?.sportId, fixtureType: 'highlights' }))"
						:label="t('show-more-highlights')"
						:icon="mdiChevronDown"
						icon-pos="left"
						variant="custom"
						root-class="max-w-full w-[200px] capitalize border-light-500 dark:border-dark-400 border rounded-full hover:bg-light-600 dark:hover:bg-dark-400"
					/>
				</div>
			</SimpleViewAccordion>
			<SimpleViewAccordion :title="t('live')">
				<EventLoadingSimple
					v-if="isLoadingLive"
					v-for="loader in Array.from({ length: 10 }).keys()"
					:key="loader"
					:class="['border-t first:border-none border-light-500 dark:border-dark-600']"
				/>
				<EventSimpleView
					:class="['border-t first:border-none border-light-500 dark:border-dark-600']"
					v-else-if="!liveError && eventIds[0] && eventIds[0].length"
					v-for="event in eventIds[0]"
					:key="`simple-combined-event-${event}`"
					:eventId="event"
				/>
				<div class="flex items-center justify-center p-2 bg-light-50 dark:bg-dark-800" v-else>{{ t("no-results-found") }}</div>
				<div
					class="flex items-center justify-center w-full p-1 border-t rounded-b bg-light-50 dark:bg-dark-800 border-t-light-300 dark:border-t-dark-700"
				>
					<Button
						@click="router.push(sportUrlBuilder({ sport: sport?.sportId, fixtureType: 'live' }))"
						:label="t('show-more-live')"
						:icon="mdiChevronDown"
						icon-pos="left"
						variant="custom"
						root-class="max-w-full w-[200px] capitalize border-light-500 dark:border-dark-400 border rounded-full hover:bg-light-600 dark:hover:bg-dark-400"
					/>
				</div>
			</SimpleViewAccordion>
		</NuxtErrorBoundary>
		<KenticoClientContent v-if="!settings.isNativeApp" />
	</div>
</template>
